import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, legacyAppUrl } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import {
  Alert,
  DataTable,
  Empty,
  Muted,
  PageHeader,
  Section,
  StatusBadge,
} from '@/components/ui'
import { createCompetencia, deleteCompetencia, fetchCompetencias, patchCompetencia } from './api'

export function CompetenciasPage() {
  const { canEdit } = useAuth()
  const qc = useQueryClient()
  const [tipo, setTipo] = useState('')
  const [busca, setBusca] = useState('')
  const [q, setQ] = useState('')
  const [nome, setNome] = useState('')
  const [novoTipo, setNovoTipo] = useState('tecnica')
  const [msg, setMsg] = useState('')

  const lista = useQuery({
    queryKey: ['rh', 'competencias', tipo, q],
    queryFn: () =>
      fetchCompetencias({
        tipo: tipo || undefined,
        busca: q || undefined,
      }),
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['rh', 'competencias'] })

  const criar = useMutation({
    mutationFn: () => createCompetencia(nome.trim(), novoTipo),
    onSuccess: async () => {
      setMsg('Competência criada.')
      setNome('')
      await invalidate()
    },
    onError: (err) => {
      setMsg(err instanceof ApiError ? err.message : 'Falha ao criar.')
    },
  })

  const toggle = useMutation({
    mutationFn: ({ id, ativo }: { id: string; ativo: boolean }) =>
      patchCompetencia(id, { ativo }),
    onSuccess: async () => {
      await invalidate()
    },
    onError: (err) => {
      setMsg(err instanceof ApiError ? err.message : 'Falha ao atualizar.')
    },
  })

  const remover = useMutation({
    mutationFn: (id: string) => deleteCompetencia(id),
    onSuccess: async () => {
      setMsg('Tag removida.')
      await invalidate()
    },
    onError: (err) => {
      setMsg(err instanceof ApiError ? err.message : 'Falha ao remover.')
    },
  })

  const dados = lista.data?.dados || []

  return (
    <section className="hi-page">
      <PageHeader
        title="Competências"
        subtitle="Banco de tags técnicas e comportamentais via API. Matriz e matching avançados no legado."
        actions={
          <a className="btn btn-secondary" href={legacyAppUrl('#/competencias')}>
            Legado
          </a>
        }
      />

      {msg ? <Alert tone="info">{msg}</Alert> : null}

      {canEdit('competencias') ? (
        <Section title="Nova tag">
          <form
            className="hi-filters"
            onSubmit={(e) => {
              e.preventDefault()
              if (!nome.trim()) return
              criar.mutate()
            }}
          >
            <input
              type="text"
              placeholder="Nome da competência"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              required
            />
            <select value={novoTipo} onChange={(e) => setNovoTipo(e.target.value)}>
              <option value="tecnica">Técnica</option>
              <option value="comportamental">Comportamental</option>
            </select>
            <button type="submit" className="btn btn-primary" disabled={criar.isPending}>
              Adicionar
            </button>
          </form>
        </Section>
      ) : null}

      <form
        className="hi-filters"
        onSubmit={(e) => {
          e.preventDefault()
          setQ(busca.trim())
        }}
      >
        <input
          type="search"
          placeholder="Buscar tag…"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        <select value={tipo} onChange={(e) => setTipo(e.target.value)}>
          <option value="">Todos os tipos</option>
          <option value="tecnica">Técnicas</option>
          <option value="comportamental">Comportamentais</option>
        </select>
        <button type="submit" className="btn btn-primary">
          Buscar
        </button>
      </form>

      <Section title="Tags" flush>
        {lista.isLoading ? <Muted>Carregando…</Muted> : null}
        {lista.isError ? (
          <Alert tone="danger">
            {lista.error instanceof ApiError ? lista.error.message : 'Falha ao listar.'}
          </Alert>
        ) : null}
        {!lista.isLoading && dados.length === 0 ? <Empty>Nenhuma competência.</Empty> : null}
        {dados.length > 0 ? (
          <DataTable>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Tipo</th>
                <th>Uso</th>
                <th>Status</th>
                {canEdit('competencias') ? <th>Ações</th> : null}
              </tr>
            </thead>
            <tbody>
              {dados.map((t) => (
                <tr key={t.id}>
                  <td>
                    <strong>{t.nome}</strong>
                  </td>
                  <td>{t.tipo_display || t.tipo}</td>
                  <td>{t.uso ?? 0}</td>
                  <td>
                    <StatusBadge tone={t.ativo === false ? 'inativo' : 'ativo'}>
                      {t.ativo === false ? 'Inativa' : 'Ativa'}
                    </StatusBadge>
                  </td>
                  {canEdit('competencias') ? (
                    <td>
                      <div className="hi-actions">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          disabled={toggle.isPending}
                          onClick={() =>
                            toggle.mutate({ id: t.id, ativo: t.ativo === false })
                          }
                        >
                          {t.ativo === false ? 'Ativar' : 'Desativar'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          disabled={remover.isPending}
                          onClick={() => {
                            if (confirm(`Remover “${t.nome}”?`)) remover.mutate(t.id)
                          }}
                        >
                          Remover
                        </button>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </DataTable>
        ) : null}
      </Section>
    </section>
  )
}

export default CompetenciasPage
